// LinkedIn não aceita HTML, markdown, ou rich text.
// Esta lib serializa o doc do Tiptap (ProseMirror JSON) pra plain text usando os truques que o LinkedIn renderiza:
//
//   - Bold: caracteres Unicode "Mathematical Sans-Serif Bold" (𝗮𝗯𝗰...)
//   - Italic: Unicode "Mathematical Sans-Serif Italic" (𝘢𝘣𝘤...)
//   - Bold+Italic: Unicode "Sans-Serif Bold Italic"
//   - Bullet list: "• " no início da linha
//   - Numbered list: "1. ", "2. " etc
//   - Links: URL plain (LinkedIn detecta e renderiza preview card automaticamente)
//   - Mentions/hashtags: já vão como texto, LinkedIn detecta
//
// Limitações:
//   - Caracteres Unicode fancy não são lidos por leitores de tela. Aviso na UI sobre acessibilidade.
//   - Não tem como linkar texto custom (só URL crua).

import type { JSONContent } from "@tiptap/react";

type StyleFlags = { bold?: boolean; italic?: boolean };

// ────────────── Unicode maps ──────────────
// Pontos de código base do alfabeto matemático Unicode.

const CHAR_MAPS = {
  bold: { lower: 0x1d5ee, upper: 0x1d5d4, digit: 0x1d7ec },          // Sans-Serif Bold
  italic: { lower: 0x1d622, upper: 0x1d608, digit: null },           // Sans-Serif Italic (sem dígitos)
  boldItalic: { lower: 0x1d656, upper: 0x1d63c, digit: null },       // Sans-Serif Bold Italic
} as const;

const transformChar = (ch: string, flags: StyleFlags): string => {
  if (!flags.bold && !flags.italic) return ch;
  const map = flags.bold && flags.italic ? CHAR_MAPS.boldItalic : flags.bold ? CHAR_MAPS.bold : CHAR_MAPS.italic;
  const code = ch.codePointAt(0)!;
  if (code >= 0x61 && code <= 0x7a) return String.fromCodePoint(map.lower + (code - 0x61));
  if (code >= 0x41 && code <= 0x5a) return String.fromCodePoint(map.upper + (code - 0x41));
  if (map.digit && code >= 0x30 && code <= 0x39) return String.fromCodePoint(map.digit + (code - 0x30));
  return ch; // não-alfanumérico ou italic-digit (não existe Unicode) → mantém
};

const transformText = (text: string, flags: StyleFlags): string => {
  return [...text].map((c) => transformChar(c, flags)).join("");
};

// ────────────── Serialização do doc ──────────────

export const serializeToLinkedIn = (doc: JSONContent): string => {
  if (!doc.content) return "";
  return doc.content.map(serializeBlock).join("\n\n").replace(/\n{3,}/g, "\n\n").trim();
};

const LIST_INDENT = "  ";

// Serializa lista (bullet/ordered) lidando com aninhamento: cada listItem pode
// conter conteúdo inline (parágrafo) E sub-listas. Sub-listas recursam com
// indentação, em vez de achatar o texto do filho no do pai.
const serializeList = (node: JSONContent, depth: number): string => {
  const ordered = node.type === "orderedList";
  const indent = LIST_INDENT.repeat(depth);
  return (node.content ?? [])
    .map((li, i) => {
      const marker = ordered ? `${i + 1}. ` : "• ";
      const inline: string[] = [];
      const nested: string[] = [];
      for (const child of li.content ?? []) {
        if (child.type === "bulletList" || child.type === "orderedList") {
          nested.push(serializeList(child, depth + 1));
        } else {
          inline.push(serializeInline(child));
        }
      }
      const head = `${indent}${marker}${inline.join(" ")}`;
      return nested.length ? `${head}\n${nested.join("\n")}` : head;
    })
    .join("\n");
};

const serializeBlock = (node: JSONContent): string => {
  switch (node.type) {
    case "paragraph":
      return serializeInline(node);
    case "heading":
      // LinkedIn não tem heading; força bold + nova linha
      return transformText(serializeInline(node), { bold: true });
    case "bulletList":
    case "orderedList":
      return serializeList(node, 0);
    case "blockquote":
      return (node.content ?? []).map((p) => `> ${serializeInline(p)}`).join("\n");
    case "hardBreak":
      return "\n";
    default:
      return serializeInline(node);
  }
};

const serializeInline = (node: JSONContent): string => {
  if (node.type === "text") {
    const flags: StyleFlags = {};
    for (const mark of node.marks ?? []) {
      if (mark.type === "bold") flags.bold = true;
      if (mark.type === "italic") flags.italic = true;
    }
    return transformText(node.text ?? "", flags);
  }
  if (node.type === "hardBreak") return "\n";
  if (node.content) return node.content.map(serializeInline).join("");
  return "";
};

// ────────────── Helpers de UI ──────────────

/** Conta chars no output final (caracteres Unicode contam como múltiplos bytes mas LinkedIn conta como 1 grapheme) */
export const countCharsForLinkedIn = (text: string): number => {
  return [...text].length;
};

/** Onde fica o cutoff "ver mais" do LinkedIn (aproximado, varia por viewport). */
export const LINKEDIN_TRUNCATE_AT = 210;

/**
 * Divide o texto no cutoff "ver mais" contando CODE POINTS (não unidades
 * UTF-16) — assim nunca racha um surrogate pair dos caracteres bold/itálico
 * astrais (que viraria "�" no corte). Usa a mesma unidade de
 * `countCharsForLinkedIn`.
 */
export const splitAtLinkedInCutoff = (
  text: string
): { before: string; after: string; truncated: boolean } => {
  const chars = [...text];
  if (chars.length <= LINKEDIN_TRUNCATE_AT) {
    return { before: text, after: "", truncated: false };
  }
  return {
    before: chars.slice(0, LINKEDIN_TRUNCATE_AT).join(""),
    after: chars.slice(LINKEDIN_TRUNCATE_AT).join(""),
    truncated: true,
  };
};

/** LinkedIn rejeita posts acima desse limite.
 *  MIRROR de backend/src/handlers/generate-candidates.ts:MAX_TOTAL. */
export const LINKEDIN_MAX_CHARS = 3000;
